/**
 * Leave a brand-specific page when the parent switches to a profile it does not belong to.
 *
 * A program and an enrollment each belong to exactly one brand. Switching profiles while
 * reading one leaves the parent looking at the other brand's content under this brand's
 * header, which is the one thing the profile switch is supposed to prevent.
 *
 * **Deliberately a navigation, not an access rule.** No RLS, no 404, no error screen: the row
 * was always readable and still is, and a parent who follows a shared link to the other
 * brand's program should see it, not a wall. This only reacts to the parent *changing their
 * mind mid-page*, and it sends them somewhere useful rather than blocking them.
 *
 * It fires on a *change* of active brand, never on a mismatch at mount. That distinction is
 * load-bearing: `activeBrand` starts at the default while `memberBrands` is still resolving,
 * so enforcing a match on mount would bounce a Kaleido-only family off their own deep link in
 * the moment before their membership loads.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrandProfile } from '../contexts/brand-profile';
import { isBrandKey } from '../config/brands';

/**
 * @param subjectBrand  The brand of the thing on screen — `programs.brand_key`. Undefined
 *                      while it loads, which suppresses the guard until it is known.
 * @param fallbackPath  Where to send the parent instead.
 */
export function useBrandScopedPage(
    subjectBrand: string | null | undefined,
    fallbackPath: string
): void {
    const { activeBrand } = useBrandProfile();
    const navigate = useNavigate();
    const lastBrand = useRef(activeBrand);

    useEffect(() => {
        const previous = lastBrand.current;
        if (previous === activeBrand) return;
        lastBrand.current = activeBrand;

        // Still loading: nothing is known to be out of scope yet.
        if (!isBrandKey(subjectBrand)) return;
        // The switch brought this page *into* scope — e.g. a parent following a Kaleido link
        // from the BHCS profile and then switching to Kaleido. Keep them where they are.
        if (subjectBrand === activeBrand) return;

        navigate(fallbackPath, { replace: true });
    }, [activeBrand, subjectBrand, fallbackPath, navigate]);
}
